import { useEffect, useMemo, useState } from 'react';
import './App.css';

const STORAGE_PASSWORD = 'Omar2500';
const SESSION_KEY = 'private-storage-unlocked';
const RESOURCES_KEY = 'private-storage-resources';

function App() {
  const [isUnlocked, setIsUnlocked] = useState(
    () => window.sessionStorage.getItem(SESSION_KEY) === 'true'
  );
  const [page, setPage] = useState('home');
  const [resources, setResources] = useState(() => readResources());
  const [message, setMessage] = useState('');

  const sortedResources = useMemo(
    () =>
      [...resources].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [resources]
  );

  useEffect(() => {
    writeResources(resources);
  }, [resources]);

  function unlock(password) {
    if (password === STORAGE_PASSWORD) {
      window.sessionStorage.setItem(SESSION_KEY, 'true');
      setIsUnlocked(true);
      setPage('home');
      setMessage('');
      return true;
    }

    return false;
  }

  async function addResource(resource) {
    setMessage('');
    setResources((current) => [resource, ...current]);
    setPage('home');
    setMessage('Resource added.');
  }

  async function deleteResource(resource) {
    const confirmed = window.confirm(`Delete "${resource.title}"?`);

    if (!confirmed) {
      return;
    }

    setMessage('');

    setResources((current) => current.filter((item) => item.id !== resource.id));
    setMessage('Resource deleted.');
  }

  async function copyValue(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(`${label} copied.`);
    } catch {
      setMessage('Copy failed. Select the value manually.');
    }
  }

  if (!isUnlocked) {
    return <WelcomePage onUnlock={unlock} />;
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Private storage</p>
          <h1>Resources</h1>
        </div>
        <nav className="nav-actions" aria-label="Primary">
          <button
            className={page === 'home' ? 'tab active' : 'tab'}
            type="button"
            onClick={() => setPage('home')}
          >
            Home
          </button>
          <button className="primary-button" type="button" onClick={() => setPage('add')}>
            Add resource
          </button>
        </nav>
      </header>

      {message && <p className="status-line">{message}</p>}

      {page === 'home' ? (
        <HomePage
          resources={sortedResources}
          onAdd={() => setPage('add')}
          onDelete={deleteResource}
          onCopy={copyValue}
        />
      ) : (
        <AddResourcePage onAdd={addResource} onCancel={() => setPage('home')} />
      )}
    </main>
  );
}

function WelcomePage({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  function handleSubmit(event) {
    event.preventDefault();
    const ok = onUnlock(password);

    if (!ok) {
      setError('Wrong password.');
    }
  }

  return (
    <main className="welcome-page">
      <section className="welcome-panel">
        <p className="eyebrow">Omar private storage</p>
        <h1>Open your device-to-device vault.</h1>
        <p className="welcome-copy">
          Upload files, images, links, and notes once, then pick them up from any device.
        </p>
        <form className="password-form" onSubmit={handleSubmit}>
          <label htmlFor="password">Storage password</label>
          <input
            id="password"
            autoFocus
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter password"
          />
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button wide" type="submit">
            Unlock storage
          </button>
        </form>
      </section>
    </main>
  );
}

function HomePage({ resources, onAdd, onDelete, onCopy }) {
  return (
    <section className="content-section">
      <div className="section-heading">
        <div>
          <h2>All resources</h2>
          <p>{resources.length} saved item{resources.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {resources.length === 0 ? (
        <div className="empty-state">
          <h3>No resources yet</h3>
          <p>Add your first image, file, link, or text note.</p>
          <button className="primary-button" type="button" onClick={onAdd}>
            Add resource
          </button>
        </div>
      ) : (
        <div className="resource-grid">
          {resources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              onDelete={onDelete}
              onCopy={onCopy}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ResourceCard({ resource, onDelete, onCopy }) {
  const isFileLike = resource.type === 'image' || resource.type === 'file';
  const displayUrl = resource.public_url || resource.content;
  const createdAt = resource.created_at
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(resource.created_at))
    : '';

  return (
    <article className="resource-card">
      <div className="resource-preview">
        {resource.type === 'image' && displayUrl ? (
          <img src={displayUrl} alt={resource.title} />
        ) : (
          <span>{typeLabel(resource.type)}</span>
        )}
      </div>
      <div className="resource-body">
        <div className="resource-title-row">
          <h3>{resource.title}</h3>
          <span className={`type-pill ${resource.type}`}>{typeLabel(resource.type)}</span>
        </div>
        {createdAt && <p className="resource-date">{createdAt}</p>}
        {resource.type === 'text' && <p className="resource-text">{resource.content}</p>}
        {resource.type === 'link' && (
          <a className="resource-link" href={resource.content} target="_blank" rel="noreferrer">
            {resource.content}
          </a>
        )}
        {isFileLike && resource.file_name && (
          <p className="resource-text">{resource.file_name}</p>
        )}
      </div>
      <div className="card-actions">
        {isFileLike && displayUrl && (
          <>
            <a className="secondary-button action-link" href={displayUrl} download>
              Download
            </a>
            <button type="button" onClick={() => onCopy(resource.file_name, 'File name')}>
              Copy name
            </button>
          </>
        )}
        {resource.type === 'link' && (
          <button type="button" onClick={() => onCopy(resource.content, 'Link')}>
            Copy link
          </button>
        )}
        {resource.type === 'text' && (
          <button type="button" onClick={() => onCopy(resource.content, 'Text')}>
            Copy text
          </button>
        )}
        <button className="danger-button" type="button" onClick={() => onDelete(resource)}>
          Delete
        </button>
      </div>
    </article>
  );
}

function AddResourcePage({ onAdd, onCancel }) {
  const [type, setType] = useState('image');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isFileLike = type === 'image' || type === 'file';

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (isFileLike && !file) {
        throw new Error('Upload a file for this resource type');
      }

      if (!isFileLike && !content.trim()) {
        throw new Error('Content is required');
      }

      const resource = {
        id: crypto.randomUUID(),
        type,
        title: title.trim(),
        content: isFileLike ? await fileToDataUrl(file) : content.trim(),
        file_name: isFileLike ? file.name : null,
        mime_type: isFileLike ? file.type : null,
        size: isFileLike ? file.size : null,
        public_url: null,
        created_at: new Date().toISOString(),
      };

      await onAdd(resource);
      setTitle('');
      setContent('');
      setFile(null);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="content-section narrow">
      <div className="section-heading">
        <div>
          <h2>Add resource</h2>
          <p>Choose the type and save it to your private collection.</p>
        </div>
      </div>

      <form className="resource-form" onSubmit={handleSubmit}>
        <label htmlFor="resource-type">Resource type</label>
        <select
          id="resource-type"
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            setContent('');
            setFile(null);
          }}
        >
          <option value="image">Image</option>
          <option value="file">File</option>
          <option value="link">Link</option>
          <option value="text">Text</option>
        </select>

        <label htmlFor="title">Title</label>
        <input
          id="title"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Name this resource"
        />

        {isFileLike ? (
          <>
            <label htmlFor="resource-file">Upload</label>
            <input
              id="resource-file"
              required
              type="file"
              accept={type === 'image' ? 'image/*' : undefined}
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
          </>
        ) : type === 'link' ? (
          <>
            <label htmlFor="content">URL</label>
            <input
              id="content"
              required
              type="url"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="https://example.com"
            />
          </>
        ) : (
          <>
            <label htmlFor="content">Text</label>
            <textarea
              id="content"
              required
              rows="7"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Paste or write text here"
            />
          </>
        )}

        {error && <p className="form-error">{error}</p>}

        <div className="form-actions">
          <button className="secondary-button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save resource'}
          </button>
        </div>
      </form>
    </section>
  );
}

function typeLabel(type) {
  const labels = {
    image: 'Image',
    file: 'File',
    link: 'Link',
    text: 'Text',
  };

  return labels[type] || 'Resource';
}

function readResources() {
  try {
    return JSON.parse(window.localStorage.getItem(RESOURCES_KEY)) || [];
  } catch {
    return [];
  }
}

function writeResources(resources) {
  window.localStorage.setItem(RESOURCES_KEY, JSON.stringify(resources));
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export default App;
